import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { Dialog, Box, Typography, IconButton, Button, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GoogleMap, useJsApiLoader, Polyline, Marker, Circle } from '@react-google-maps/api';
import CloseIcon from '@material-ui/icons/Close';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import ReplayIcon from '@material-ui/icons/Replay';
import SpeedIcon from '@material-ui/icons/Speed';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../App';
import { cleanTrail } from '../../utils/trailClean';
import { buildVehicleMarkerIcon, onVehicleIconLoaded } from '../../utils/vehicleIcons';

// Replay of a route's actual GPS trail. Loads the Traccar/EcooGPS history
// for the time the route was live (started → completed) and animates a
// vehicle marker along it, with playback controls (play/pause, speed,
// scrub bar). The planned route is drawn as a faint blue line behind the
// trail so the operator can compare actual vs planned at a glance.

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
// Must match MapViewGoogle's loader options exactly — @react-google-maps/api
// throws "Loader must not be called again with different options" if any
// component on the page mounts useJsApiLoader with a different config.
const LIBRARIES = ['places'];

const SPEEDS = [1, 2, 5, 10, 25];

const useStyles = makeStyles(() => ({
  paper: {
    width: '92vw', height: '92vh', maxWidth: 1400, borderRadius: 14,
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 14,
    borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 60%, #e0e7ff 100%)',
  },
  // Banda esquerda do cabeçalho com ícone redondo (replay) — dá peso
  // visual e identifica o tipo de janela à primeira vista.
  headerIcon: {
    width: 38, height: 38, borderRadius: 12,
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
    flexShrink: 0,
  },
  title: { fontSize: '1.0rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  // Linha de badges: matrícula, motorista, estado, data. Cada badge é
  // uma "pill" leve com ícone + texto. Espaçamento e cores escolhidos
  // para o cabeçalho ler como o de uma ficha de carro premium.
  headerBadges: {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
    flexWrap: 'wrap',
  },
  headerBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', borderRadius: 999,
    fontSize: '0.66rem', fontWeight: 700,
    background: '#fff', border: '1px solid #e2e8f0',
    color: '#334155',
  },
  headerBadgePlate: {
    background: '#0f172a', color: '#fff', border: '1px solid #0f172a',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    letterSpacing: '0.04em',
  },
  headerBadgeStatusDone: {
    background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0',
  },
  headerBadgeStatusLive: {
    background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
  },
  headerBadgeStatusOther: {
    background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
  },
  headerStats: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 999,
    fontSize: '0.66rem', fontWeight: 600,
    background: 'rgba(59,130,246,0.08)', color: '#1d4ed8',
    border: '1px solid rgba(59,130,246,0.2)',
  },
  body: { flex: 1, position: 'relative', minHeight: 0 },
  loading: {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.7)', zIndex: 5, gap: 10,
  },
  empty: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 10, color: '#64748b', textAlign: 'center', padding: 24,
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
  },
  emptyCard: {
    background: '#fff', borderRadius: 16,
    padding: '28px 32px', maxWidth: 480,
    boxShadow: '0 8px 28px rgba(15,23,42,0.08)',
    border: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  },
  emptyIcon: {
    width: 64, height: 64, borderRadius: 18,
    background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32,
    border: '1px solid #cbd5e1',
  },
  emptyTitle: {
    fontSize: '1.0rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em',
  },
  emptyBody: {
    fontSize: '0.78rem', color: '#475569', lineHeight: 1.5, textAlign: 'center',
  },
  emptyHint: {
    fontSize: '0.7rem', color: '#64748b',
    background: '#f8fafc', padding: '8px 14px', borderRadius: 8,
    border: '1px dashed #cbd5e1', marginTop: 4,
  },
  controls: {
    padding: '10px 16px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    display: 'grid', gap: 8,
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
  },
  playBtn: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff', borderRadius: 999, padding: 8,
    boxShadow: '0 3px 10px rgba(59,130,246,0.35)',
    '&:hover': { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' },
  },
  speedRow: { display: 'flex', alignItems: 'center', gap: 6 },
  speedBtn: {
    minWidth: 0, padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700,
    borderRadius: 999, textTransform: 'none', border: '1px solid #e2e8f0',
    color: '#475569', background: '#fff',
  },
  speedBtnActive: {
    background: '#1e293b', color: '#fff',
    border: '1px solid #1e293b',
    '&:hover': { background: '#0f172a' },
  },
  hud: {
    position: 'absolute', bottom: 12, left: 12, zIndex: 4,
    background: 'rgba(15,23,42,0.85)', color: '#fff',
    padding: '8px 14px', borderRadius: 10,
    fontSize: '0.74rem', fontWeight: 700,
    boxShadow: '0 6px 20px rgba(15,23,42,0.18)',
    display: 'grid', gap: 2,
  },
  hudRow: { display: 'flex', alignItems: 'center', gap: 8 },
  hudLabel: { color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' },
  scrubMark: { fontSize: '0.6rem', color: '#64748b' },
  // Stack of visited-OT cards. Top-left agora que a HUD desceu para o
  // canto inferior esquerdo. Cada card desliza da esquerda ao chegar à OT.
  otStack: {
    position: 'absolute', left: 12, top: 12, zIndex: 4,
    display: 'flex', flexDirection: 'column', gap: 6,
    maxHeight: 'calc(100% - 110px)', overflowY: 'auto',
    paddingRight: 4, width: 280,
  },
  otCard: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderLeft: '4px solid #16a34a',
    borderRadius: 10,
    padding: '8px 12px',
    boxShadow: '0 6px 20px rgba(15,23,42,0.10)',
    display: 'grid', gap: 2,
    animation: '$slideInOt 0.32s ease-out',
  },
  '@keyframes slideInOt': {
    '0%': { opacity: 0, transform: 'translateX(-20px)' },
    '100%': { opacity: 1, transform: 'translateX(0)' },
  },
  otCardHeader: { display: 'flex', alignItems: 'center', gap: 6 },
  otSeq: {
    width: 22, height: 22, borderRadius: '50%',
    background: '#16a34a', color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.66rem', fontWeight: 800, flexShrink: 0,
  },
  otRef: { fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  otStatus: {
    fontSize: '0.52rem', fontWeight: 800,
    padding: '1px 7px', borderRadius: 999,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  otName: { fontSize: '0.7rem', color: '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  otAddr: { fontSize: '0.6rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Comparative metrics panel — top-right corner, premium dark glass look.
  metrics: {
    position: 'absolute', top: 12, right: 12, zIndex: 4,
    background: 'rgba(15,23,42,0.88)',
    color: '#fff', padding: '10px 14px', borderRadius: 12,
    boxShadow: '0 8px 24px rgba(15,23,42,0.22)',
    minWidth: 240, maxWidth: 320,
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  metricsTitle: {
    fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
    marginBottom: 6,
  },
  metricsRow: {
    display: 'grid', gridTemplateColumns: '78px 1fr 1fr',
    columnGap: 10, alignItems: 'baseline',
    padding: '3px 0',
  },
  metricsLabel: { fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600 },
  metricsPlan:   { fontSize: '0.72rem', color: '#93c5fd', fontWeight: 700, textAlign: 'right' },
  metricsReal:   { fontSize: '0.72rem', color: '#fca5a5', fontWeight: 700, textAlign: 'right' },
  metricsDelta: {
    fontSize: '0.62rem', fontWeight: 600,
    textAlign: 'right', color: 'rgba(255,255,255,0.7)',
  },
  metricsBig: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginTop: 8, paddingTop: 8,
    borderTop: '1px solid rgba(255,255,255,0.10)',
  },
  metricsBigLabel: { fontSize: '0.55rem', color: 'rgba(255,255,255,0.55)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  metricsBigValue: { fontSize: '0.96rem', color: '#fbbf24', fontWeight: 800, marginLeft: 'auto', letterSpacing: '0.02em' },
  followToggle: {
    position: 'absolute', bottom: 12, right: 12, zIndex: 4,
    background: '#fff', borderRadius: 999,
    padding: '4px 12px',
    boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
    fontSize: '0.66rem', fontWeight: 700,
    color: '#1e293b', textTransform: 'none',
    border: '1px solid #e2e8f0',
    '&:hover': { background: '#f8fafc' },
  },
  followToggleActive: {
    background: '#1e293b', color: '#fff',
    border: '1px solid #1e293b',
    '&:hover': { background: '#0f172a' },
  },
}));

const mapContainerStyle = { width: '100%', height: '100%' };
// Map options tuned for "max detail" — roadmap default (street names,
// labels, POIs), traffic layer toggle, scale bar, and a map type control
// so the operator can switch to satellite/hybrid when they want to see
// terrain or buildings during the replay.
const mapOptions = {
  disableDefaultUI: false,
  mapTypeControl: true,
  streetViewControl: true,
  fullscreenControl: false,
  zoomControl: true,
  scaleControl: true,
  rotateControl: true,
  gestureHandling: 'greedy',
  mapTypeId: 'roadmap',
  tilt: 0,
  // No `styles` array — the default Google Maps styling already shows
  // every street name, sign, POI and transit label. Anything we add in
  // a custom style only hides things.
};

// Vehicle marker — bubble with plate + (optional) driver name on top,
// truck silhouette below rotating with the bearing. Mirrors the visual
// language used in [MapViewGoogle.js] when "ver veículos" is toggled,
// so the operator sees the same icon they're already familiar with.
//
// The bubble stays upright (text always readable) — only the truck box
// rotates. We embed the rotation directly in the SVG `<g transform=...>`
// so Google Maps just renders a static image per frame.
//
// Generating a fresh SVG every heading change isn't free, but the
// playback loop throttles heading updates (only changes when bearing
// shifts by more than ~3°) so we get at most a handful of regenerations
// per second.
// Marcador do veículo no replay — ícone EcooGPS do tipo de veículo
// (rodado pelo rumo) + perna + bolha com matrícula·motorista. Estado:
// em movimento → verde, em pausa do replay → cinzento.
function buildVehicleIcon({ plate, driverName, heading, state, type }) {
  if (typeof window === 'undefined' || !window.google) return null;
  const STATUS = { moving: 'green', idle: 'yellow', stopped: 'red', playback: 'gray' };
  const status = STATUS[state] || 'gray';
  const plateUp = (plate || '').toUpperCase() || '—';
  const driver = (driverName || '').trim().split(/\s+/).slice(0, 2).join(' ').slice(0, 14);
  const label = driver ? `${plateUp} · ${driver}` : plateUp;
  const mi = buildVehicleMarkerIcon({ label, type, status, heading });
  return {
    url: mi.url,
    scaledSize: new window.google.maps.Size(mi.w, mi.h),
    anchor: new window.google.maps.Point(mi.ax, mi.ay),
  };
}

function escapeXml(s) {
  return String(s).replace(/[<>&"']/g, c => (
    { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]
  ));
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Gráfico de velocidade com scrubbing bidirecional + janela (2 pegas) ──
// Substitui o slider simples por um gráfico ao estilo EcooGPS: X = tempo
// (índice do trail), Y = velocidade, faixa de estado (verde=condução,
// amarelo=ralenti, vermelho=parado). O cursor (playhead) arrasta-se para a
// frente e para trás; as 2 pegas em baixo isolam um intervalo, e a
// reprodução respeita essa janela. Trabalha só com os dados que já existem.
function SpeedScrubChart({ trail, idx, onSeek, sel, onSelChange, disabled }) {
  const svgRef = useRef(null);
  const dragRef = useRef(null); // 'playhead' | 'a' | 'b' | null
  const N = trail.length;
  const W = 1000, H = 78;
  const chartTop = 6, chartBot = 50, barTop = 54, barBot = 62;
  const lastIdx = Math.max(1, N - 1);

  const maxSpeed = useMemo(() => {
    let m = 1;
    for (let i = 0; i < N; i++) { const s = trail[i]?.speed || 0; if (s > m) m = s; }
    return Math.max(40, m);
  }, [trail, N]);

  const xOf = (i) => (i / lastIdx) * W;
  const yOf = (s) => chartBot - (Math.max(0, Math.min(s, maxSpeed)) / maxSpeed) * (chartBot - chartTop);

  const areaPath = useMemo(() => {
    if (N < 2) return '';
    const step = Math.max(1, Math.floor(N / 500));
    let d = `M 0 ${chartBot}`;
    for (let i = 0; i < N; i += step) d += ` L ${xOf(i).toFixed(1)} ${yOf(trail[i]?.speed || 0).toFixed(1)}`;
    d += ` L ${xOf(N - 1).toFixed(1)} ${yOf(trail[N - 1]?.speed || 0).toFixed(1)}`;
    d += ` L ${W} ${chartBot} Z`;
    return d;
  }, [trail, N, maxSpeed]); // eslint-disable-line react-hooks/exhaustive-deps

  const stateRuns = useMemo(() => {
    if (N < 2) return [];
    const step = Math.max(1, Math.floor(N / 500));
    const colorAt = (s) => (s > 5 ? '#16a34a' : (s > 0.6 ? '#eab308' : '#ef4444'));
    const runs = []; let start = 0; let col = colorAt(trail[0]?.speed || 0);
    for (let i = step; i < N; i += step) {
      const c = colorAt(trail[i]?.speed || 0);
      if (c !== col) { runs.push({ x0: xOf(start), x1: xOf(i), col }); start = i; col = c; }
    }
    runs.push({ x0: xOf(start), x1: W, col });
    return runs;
  }, [trail, N]); // eslint-disable-line react-hooks/exhaustive-deps

  const posFromEvent = (e) => {
    const svg = svgRef.current; if (!svg) return { i: 0, yFrac: 0 };
    const rect = svg.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const yFrac = (e.clientY - rect.top) / rect.height;
    return { i: frac * lastIdx, yFrac };
  };

  const onDown = (e) => {
    if (disabled || N < 2) return;
    const { i, yFrac } = posFromEvent(e);
    const grab = lastIdx * 0.05;
    const da = Math.abs(i - sel.a), db = Math.abs(i - sel.b);
    // Metade inferior + perto de uma pega → arrasta a pega; senão → cursor.
    if (yFrac > 0.5 && Math.min(da, db) < grab) {
      dragRef.current = da <= db ? 'a' : 'b';
    } else {
      dragRef.current = 'playhead';
      onSeek(i);
    }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    const { i } = posFromEvent(e);
    const gap = lastIdx * 0.01;
    if (dragRef.current === 'playhead') onSeek(i);
    else if (dragRef.current === 'a') onSelChange(Math.min(i, sel.b - gap), sel.b);
    else onSelChange(sel.a, Math.max(i, sel.a + gap));
  };
  const onUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const xa = xOf(sel.a), xb = xOf(sel.b), xi = xOf(Math.max(0, Math.min(idx, lastIdx)));

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
      style={{ touchAction: 'none', cursor: disabled ? 'default' : 'pointer', userSelect: 'none', display: 'block' }}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
      <defs>
        <linearGradient id="spdFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="#f8fafc" rx="6" />
      {areaPath && <path d={areaPath} fill="url(#spdFill)" stroke="#3b82f6" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />}
      {stateRuns.map((r, k) => (
        <rect key={k} x={r.x0} y={barTop} width={Math.max(0.5, r.x1 - r.x0)} height={barBot - barTop} fill={r.col} />
      ))}
      {/* zonas fora da janela seleccionada (esbatidas) */}
      <rect x="0" y="0" width={Math.max(0, xa)} height={H} fill="#0f172a" opacity="0.10" />
      <rect x={xb} y="0" width={Math.max(0, W - xb)} height={H} fill="#0f172a" opacity="0.10" />
      {/* moldura da janela */}
      <rect x={xa} y="2" width={Math.max(1, xb - xa)} height={H - 4} fill="none" stroke="#ef4444" strokeWidth="1.5" rx="4" vectorEffect="non-scaling-stroke" />
      {/* pegas (início/fim da janela) */}
      {[xa, xb].map((hx, k) => (
        <g key={k}>
          <line x1={hx} y1="2" x2={hx} y2={H - 2} stroke="#ef4444" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          <circle cx={hx} cy={H - 9} r="6.5" fill="#fff" stroke="#ef4444" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </g>
      ))}
      {/* cursor (playhead) */}
      <line x1={xi} y1="2" x2={xi} y2={barBot} stroke="#1e293b" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <circle cx={xi} cy={chartTop} r="5" fill="#1e293b" stroke="#fff" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Bearing between two coords (deg, 0=N, 90=E). Used to rotate the icon.
function bearing(a, b) {
  if (!a || !b) return 0;
  const toRad = (d) => d * Math.PI / 180;
  const toDeg = (r) => r * 180 / Math.PI;
  const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
  const Δλ = toRad(b.lng - a.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function haversineM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Distância máxima (em metros) entre um fix GPS e o vértice mais próximo
// da polilinha planeada para considerar que o motorista cumpriu o
// trajecto naquele ponto. Acima disto = desvio (pinta-se a vermelho).
// 30 m apanha bem ruído de GPS urbano sem pintar acidentalmente de
// vermelho zonas onde o motorista cumpriu mas a polilinha planeada tem
// vértices mais espaçados (autovia, periferia).
const MATCH_THRESHOLD_M = 30;

// Parte uma lista de pontos em "runs" contíguos consoante a flag
// `matched` (true → cumpriu, false → desvio). Cada run partilha o
// ponto da junção com o seguinte para que as polilinhas se liguem
// visualmente sem buracos. Saída: [{ path: [{lat,lng}], matched }].
function buildMatchRuns(points, matched) {
  if (!points || points.length < 2) return [];
  const runs = [];
  let curPath = [{ lat: points[0].lat, lng: points[0].lng }];
  let curMatched = !!matched[0];
  for (let i = 1; i < points.length; i++) {
    const p = { lat: points[i].lat, lng: points[i].lng };
    if (!!matched[i] === curMatched) {
      curPath.push(p);
    } else {
      curPath.push(p); // junction shared with next run
      if (curPath.length > 1) runs.push({ path: curPath, matched: curMatched });
      curPath = [p];
      curMatched = !!matched[i];
    }
  }
  if (curPath.length > 1) runs.push({ path: curPath, matched: curMatched });
  return runs;
}

// Pin numerado para uma paragem de serviço (OT) — círculo com a
// sequência da OT, contornado a branco para ressaltar sobre qualquer
// fundo de mapa. Cor do círculo varia com o estado da OT.
function otStopMarker(seq, color) {
  if (typeof window === 'undefined' || !window.google) return null;
  const txt = String(seq || '?');
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <path d="M 18 2 C 9 2 3 9 3 17 C 3 25 18 42 18 42 C 18 42 33 25 33 17 C 33 9 27 2 18 2 Z"
            fill="${color}" stroke="#fff" stroke-width="2.5"/>
      <circle cx="18" cy="17" r="8" fill="#fff"/>
      <text x="18" y="20" text-anchor="middle"
            font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif"
            font-size="11" font-weight="800" fill="${color}">${txt}</text>
    </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(28, 34),
    anchor: new window.google.maps.Point(14, 34),
  };
}

// House marker icon for origin/destination — far more recognisable than
// a coloured dot.
function houseMarker(color) {
  if (typeof window === 'undefined' || !window.google) return null;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">
      <path d="M 22 2 C 11 2 4 10 4 20 C 4 30 22 50 22 50 C 22 50 40 30 40 20 C 40 10 33 2 22 2 Z"
            fill="${color}" stroke="#fff" stroke-width="2.5"/>
      <path d="M 22 11 L 13 19 L 13 28 L 19 28 L 19 23 L 25 23 L 25 28 L 31 28 L 31 19 Z"
            fill="#fff"/>
    </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(34, 40),
    anchor: new window.google.maps.Point(17, 40),
  };
}

export default function RoutePlaybackDialog({ open, onClose, routeId, routeName }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trail, setTrail] = useState([]); // [{lat, lng, fixTime, speed, address}]
  const [planned, setPlanned] = useState([]); // [{lat, lng}]
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [stops, setStops] = useState([]);
  // Metadados da rota — alimentam o cabeçalho (badges) e o empty state
  // explicativo. routeStatus indica draft/in_progress/completed; as datas
  // actuais são as do tempo de execução real (quando começou e acabou no
  // terreno), usadas para diagnosticar trails vazios (rota antiga vs.
  // veículo sem fixes).
  const [routeStatus, setRouteStatus] = useState(null);
  const [actualStartedAt, setActualStartedAt] = useState(null);
  const [actualCompletedAt, setActualCompletedAt] = useState(null);
  // Vehicle/driver labels for the marker bubble — pulled from the route detail.
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [driverName, setDriverName] = useState('');
  // Tipo de veículo (categoria EcooGPS) — para o ícone do marcador.
  // Vem do feed de veículos; emparelha pela matrícula.
  const liveVehicles = useSelector(s => s.vehicles?.list || []);
  // Tipo guardado na rota (fallback quando o veículo não está no feed live).
  const [routeVehicleType, setRouteVehicleType] = useState(null);
  const vehicleType = useMemo(() => {
    // 1.º o feed live (tipo EcooGPS mais actual → ícone idêntico ao planeamento)
    if (vehiclePlate) {
      const veh = liveVehicles.find(x => (x.plate || '').toUpperCase() === vehiclePlate.toUpperCase());
      if (veh?.type) return veh.type;
    }
    // 2.º o tipo persistido na rota (replays de rotas antigas/veículo offline)
    return routeVehicleType || null;
  }, [liveVehicles, vehiclePlate, routeVehicleType]);
  // Re-render quando um ícone de veículo (EcooGPS) acaba de carregar.
  const [, setIconTick] = useState(0);
  useEffect(() => onVehicleIconLoaded(() => setIconTick(t => t + 1)), []);
  // Planned distance/duration from HERE/Google (used by the metrics panel).
  const [plannedDistM, setPlannedDistM] = useState(0);
  const [plannedDurS, setPlannedDurS] = useState(0);
  // "Follow camera" mode — auto-pan to the moving vehicle. User can
  // pause it by clicking on the map (we detect a manual drag/zoom).
  const [followCamera, setFollowCamera] = useState(true);
  // Smoothed heading so the truck doesn't twitch from GPS jitter when
  // the vehicle is barely moving. Updated only when bearing changes
  // meaningfully (>3°) — avoids regenerating the SVG icon every frame.
  const [smoothedHeading, setSmoothedHeading] = useState(0);
  // Pulse halo radius (meters) — driven by a CSS-free rAF tween so we
  // get a smooth growth/shrink that pulses ~1.5s.
  const [pulseRadius, setPulseRadius] = useState(0);
  // Orders attached to this route — fetched separately because GET
  // /routes/:id doesn't include OTs. Used to (a) build the title with
  // their customRefs and (b) trigger info cards as the animation
  // marker passes by each delivery point.
  const [orders, setOrders] = useState([]);
  const [visitedOrderIds, setVisitedOrderIds] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  // Janela seleccionada no gráfico (2 pegas): {a, b} em unidades de índice. A
  // reprodução limita-se a este intervalo. Reposta ao carregar novo trail.
  const [sel, setSel] = useState({ a: 0, b: 0 });
  const selRef = useRef(sel);
  useEffect(() => { selRef.current = sel; }, [sel]);
  const mapRef = useRef(null);
  const fittedRef = useRef(false);

  // Reset everything when dialog reopens.
  useEffect(() => {
    if (!open) {
      setTrail([]); setPlanned([]); setIdx(0);
      setPlaying(false); setError(null);
      setOrders([]); setVisitedOrderIds([]);
      setVehiclePlate(''); setDriverName(''); setRouteVehicleType(null);
      setPlannedDistM(0); setPlannedDurS(0);
      setFollowCamera(true); setSmoothedHeading(0); setPulseRadius(0);
      setRouteStatus(null); setActualStartedAt(null); setActualCompletedAt(null);
      fittedRef.current = false;
    }
  }, [open]);

  // Fetch route + actual trail when opening.
  useEffect(() => {
    if (!open || !routeId || !token) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [routeRes, trailRes, ordersRes] = await Promise.all([
          fetch(`/planner-api/routes/${routeId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
          fetch(`/planner-api/routes/${routeId}/actual-trail`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
          fetch(`/planner-api/transport-orders?assignedRouteId=${routeId}&pageSize=200`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
        ]);
        if (cancelled) return;
        if (!trailRes || trailRes.error) {
          setError(trailRes?.error || 'Erro a obter percurso real do veículo.');
          setLoading(false);
          return;
        }
        // /actual-trail returns { points: [{lat,lng,timestamp,speed,address}], ... }
        // cleanTrail descarta fixes lixo do Traccar (0,0, saltos absurdos)
        // que inflavam a distância real e o desvio máximo.
        const points = cleanTrail(
          (trailRes.points || trailRes.trail || []).map(p => ({
            lat: p.lat, lng: p.lng,
            fixTime: p.timestamp || p.fixTime,
            speed: p.speed || 0,
            address: p.address || null,
          }))
        );
        setTrail(points);
        if (routeRes) {
          const pl = (routeRes.routeData?.computedRoute?.geometry?.coordinates || []).map(c => ({ lat: c[1], lng: c[0] }));
          setPlanned(pl);
          // Planned distance/duration come from the persisted route. Falls back
          // to a sum of polyline haversine if the totals weren't stored.
          const cr = routeRes.routeData?.computedRoute || {};
          setPlannedDistM(Number(cr.distance || routeRes.totalDistanceM || 0));
          setPlannedDurS(Number(cr.duration || routeRes.totalDurationS || 0));
          // Metadados — alimentam o cabeçalho (pill de estado, data) e o
          // empty state quando o trail vem vazio (calcula a "idade" da
          // execução para distinguir "EcooGPS purgou" de "veículo offline").
          setRouteStatus(routeRes.status || null);
          setActualStartedAt(routeRes.routeData?.actualStartedAt || routeRes.sentToVehicleAt || null);
          setActualCompletedAt(routeRes.routeData?.actualCompletedAt || routeRes.completedAt || null);
          // Vehicle plate + driver name — populates the moving marker's
          // bubble. Same lookup order apiRouteToFrontend uses, so we're
          // consistent with the planner.
          const rd2 = routeRes.routeData || {};
          setVehiclePlate(routeRes.vehicle?.plate || rd2.vehicleInfo?.plate || routeRes.vehiclePlate || '');
          setDriverName(routeRes.driver?.name || rd2.driverInfo?.name || routeRes.driverName || '');
          // Tipo de veículo guardado na rota — fallback do ícone quando o
          // veículo já não está no feed live (rotas antigas/concluídas), para
          // o replay mostrar o mesmo ícone do mapa de planeamento.
          setRouteVehicleType(routeRes.vehicle?.type || rd2.vehicleInfo?.type || null);
          if (Array.isArray(routeRes.origin?.coordinates)) {
            setOrigin({ lat: routeRes.origin.coordinates[1], lng: routeRes.origin.coordinates[0], name: routeRes.origin.text || routeRes.origin.name });
          }
          if (Array.isArray(routeRes.destination?.coordinates)) {
            setDestination({ lat: routeRes.destination.coordinates[1], lng: routeRes.destination.coordinates[0], name: routeRes.destination.text || routeRes.destination.name });
          }
          const wps = (routeRes.waypoints || [])
            .filter(w => Array.isArray(w?.coordinates))
            .map((w, i) => ({ lat: w.coordinates[1], lng: w.coordinates[0], name: w.name || `${i + 1}`, idx: i + 1 }));
          setStops(wps);
        }
        // Build OT list with delivery coordinates so we can detect when
        // the moving marker passes by each one. We accept either
        // { items: [...] } or a raw array depending on the endpoint shape.
        const otList = Array.isArray(ordersRes) ? ordersRes
          : (ordersRes?.items || ordersRes?.orders || ordersRes?.data || []);
        const otsWithCoords = (otList || [])
          .map((o, i) => ({
            id: o.id,
            seq: i + 1,
            customRef: (o.customRef || '').trim() || o.orderNumber || `OT-${o.id}`,
            deliveryName: o.deliveryName || o.deliveryAddress || '—',
            deliveryAddress: o.deliveryAddress || null,
            status: o.status || 'pending',
            serviceType: o.serviceType || null,
            lat: o.deliveryLat != null ? Number(o.deliveryLat) : null,
            lng: o.deliveryLng != null ? Number(o.deliveryLng) : null,
          }))
          .filter(o => Number.isFinite(o.lat) && Number.isFinite(o.lng));
        setOrders(otsWithCoords);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Erro a carregar percurso.');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, routeId, token]);

  // Fit bounds once after loading. Includes origin and destination
  // unconditionally so the "casa" markers are always visible even when
  // the GPS history doesn't reach all the way home.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || fittedRef.current) return;
    if (trail.length < 2 && planned.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    [...trail, ...planned].forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (origin) bounds.extend(origin);
    if (destination) bounds.extend(destination);
    stops.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
    mapRef.current.fitBounds(bounds, { top: 60, bottom: 80, left: 60, right: 60 });
    fittedRef.current = true;
  }, [isLoaded, trail, planned, origin, destination, stops]);

  // playTrail = real GPS trail + (optionally) a synthetic "return home"
  // segment at the end. If the driver pressed "complete route" before
  // physically arriving at the home depot, the GPS history ends short
  // of the destination — we extend it so the animation always lands at
  // home and operators can see the final leg. The synthetic part is
  // drawn dashed/grey so it is clearly distinguished from real GPS.
  // playTrail = APENAS o trajeto GPS real. O troço sintético de "regresso a
  // casa" (linha tracejada até ao local de partida) foi removido a pedido —
  // confundia o operador (parecia um percurso que não aconteceu). A animação
  // termina no último fix GPS real.
  const playTrail = trail;
  // Ao (re)carregar o trail, a janela abrange tudo (sem restrição).
  useEffect(() => { setSel({ a: 0, b: Math.max(0, playTrail.length - 1) }); }, [playTrail.length]);
  const syntheticStartIdx = useMemo(
    () => playTrail.findIndex(p => p.synthetic),
    [playTrail]
  );

  // Animation loop. Default rate: 0.7 fixes/second × speed multiplier
  // — a 1× uma rota de 42 fixes toma ~60s (~1 fix por 1.5s), cadência
  // confortável para o olho acompanhar a posição do veículo. O operador
  // que queira ver depressa carrega 2×/5×/10×/25×. Para trails muito
  // curtos (poucos fixes) garante 25s mínimo a 1× para não terminar
  // antes do operador conseguir ler o HUD.
  useEffect(() => {
    if (!playing || playTrail.length < 2) return;
    let raf;
    let last = performance.now();
    const naiveTick = 0.7 * speed;
    const naiveTotalSec = (playTrail.length - 1) / naiveTick;
    const minTotalSec = 25 / speed;
    const tickPerSecond = naiveTotalSec >= minTotalSec
      ? naiveTick
      : (playTrail.length - 1) / minTotalSec;
    function loop(now) {
      const dt = (now - last) / 1000;
      last = now;
      setIdx(prev => {
        const next = prev + dt * tickPerSecond;
        // Pára no fim da JANELA seleccionada (não no fim absoluto do trail).
        const cap = selRef.current.b || (playTrail.length - 1);
        if (next >= cap) {
          setPlaying(false);
          return cap;
        }
        return next;
      });
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, playTrail.length]);

  const intIdx = Math.floor(idx);
  const fracIdx = idx - intIdx;
  const cur = playTrail[intIdx] || null;
  const next = playTrail[intIdx + 1] || cur;
  // Interpolate position so the marker glides between fixes.
  const animPos = useMemo(() => {
    if (!cur) return null;
    if (!next || cur === next) return { lat: cur.lat, lng: cur.lng };
    return {
      lat: cur.lat + (next.lat - cur.lat) * fracIdx,
      lng: cur.lng + (next.lng - cur.lng) * fracIdx,
    };
  }, [cur, next, fracIdx]);
  const heading = useMemo(() => bearing(cur, next), [cur, next]);
  // Smoothed heading — only updates when the new bearing differs by >3°
  // from the last applied value, so the SVG icon doesn't regenerate on
  // every micro-jitter. Critical for performance during playback.
  useEffect(() => {
    if (!cur || !next) return;
    const diff = Math.abs(((heading - smoothedHeading + 540) % 360) - 180);
    if (diff > 3) setSmoothedHeading(heading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heading]);

  // Pulse halo — rAF tween between min/max radius. Always running while
  // the dialog is open so the marker feels alive even when paused.
  useEffect(() => {
    if (!open) return;
    let raf;
    let t0 = performance.now();
    function loop(now) {
      const elapsed = (now - t0) / 1000;
      // ~1.4s period — slightly slower than a heartbeat for a calmer pulse.
      const phase = (elapsed % 1.4) / 1.4;
      // Ease-out: grows fast then fades out (more "radar ping" feel).
      const r = 8 + 26 * phase;
      setPulseRadius(r);
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [open]);
  // Halo opacity follows the same phase — fades from 0.45 to 0.
  const pulseOpacity = useMemo(() => {
    // Re-derive from radius (avoids a second state update per frame).
    const phase = Math.max(0, Math.min(1, (pulseRadius - 8) / 26));
    return 0.45 * (1 - phase);
  }, [pulseRadius]);
  // Trail real (sem o troço sintético do regresso a casa) — base para
  // a classificação cumpriu/desvio. O sintético é desenhado tracejado
  // a cinzento como antes.
  const realTrail = useMemo(() => {
    const end = syntheticStartIdx >= 0 ? syntheticStartIdx : playTrail.length;
    return playTrail.slice(0, end);
  }, [playTrail, syntheticStartIdx]);

  // Para cada fix do trail real: true se está a menos de MATCH_THRESHOLD_M
  // do vértice mais próximo da polilinha planeada (motorista cumpriu),
  // false se desviou. Usa nearest-vertex com early-out — O(n*m) no pior
  // caso mas tipicamente sai cedo porque os fixes estão quase sempre
  // perto da rota. Cache invalida quando o trail ou o planeado mudam,
  // que é raro (uma vez no load).
  const trailMatched = useMemo(() => {
    if (planned.length < 2) return realTrail.map(() => false);
    return realTrail.map(p => {
      let min = Infinity;
      for (let j = 0; j < planned.length; j++) {
        const d = haversineM(p, planned[j]);
        if (d < min) min = d;
        if (min < MATCH_THRESHOLD_M) break;
      }
      return min < MATCH_THRESHOLD_M;
    });
  }, [realTrail, planned]);

  // Runs contíguos verde/vermelho para o trail INTEIRO — desenhados em
  // opacidade baixa, dando ao operador a vista geral do que falta antes
  // da animação chegar lá.
  const allRealRuns = useMemo(
    () => buildMatchRuns(realTrail, trailMatched),
    [realTrail, trailMatched]
  );

  // Runs contíguos verde/vermelho para a parte já percorrida pela
  // animação — desenhados em opacidade sólida POR CIMA de allRealRuns.
  // O efeito é o trajeto "preencher-se" à medida que o veículo passa.
  const passedRealRuns = useMemo(() => {
    const cap = Math.max(0, Math.min(intIdx + 1, realTrail.length));
    if (cap < 2) return [];
    return buildMatchRuns(realTrail.slice(0, cap), trailMatched.slice(0, cap));
  }, [realTrail, trailMatched, intIdx]);

  // Troço sintético "regresso a casa" (tracejado cinzento) — só a
  // porção já animada, mesmo comportamento de antes.
  const passedSyntheticTrail = useMemo(() => {
    if (syntheticStartIdx < 0 || intIdx < syntheticStartIdx - 1) return [];
    const start = Math.max(0, syntheticStartIdx - 1);
    return playTrail.slice(start, intIdx + 1).map(p => ({ lat: p.lat, lng: p.lng }));
  }, [playTrail, intIdx, syntheticStartIdx]);

  // Maximum deviation between the actual GPS trail and the planned
  // polyline. Computed once on load. We sample at most 200 trail points
  // and for each find the nearest planned vertex (O(n*m) but bounded).
  const deviationMax = useMemo(() => {
    if (trail.length < 2 || planned.length < 2) return null;
    const step = Math.max(1, Math.floor(trail.length / 200));
    let best = { dist: 0, point: null };
    for (let i = 0; i < trail.length; i += step) {
      const t = trail[i];
      let nearest = Infinity;
      for (let j = 0; j < planned.length; j++) {
        const d = haversineM(t, planned[j]);
        if (d < nearest) nearest = d;
      }
      if (nearest > best.dist) best = { dist: nearest, point: { lat: t.lat, lng: t.lng } };
    }
    return best.dist > 25 ? best : null; // ignore deviations under 25m (GPS noise)
  }, [trail, planned]);

  // Actual distance — sum of haversine between consecutive real-trail
  // fixes. Excludes the synthetic return-home leg so we compare apples
  // to apples against the planned distance.
  const actualDistM = useMemo(() => {
    if (trail.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < trail.length; i++) {
      total += haversineM(trail[i - 1], trail[i]);
    }
    return total;
  }, [trail]);
  // Total elapsed seconds since start.
  const elapsedSec = useMemo(() => {
    if (!cur || !trail.length) return 0;
    return Math.max(0, (new Date(cur.fixTime).getTime() - new Date(trail[0].fixTime).getTime()) / 1000);
  }, [cur, trail]);
  const totalSec = useMemo(() => {
    if (trail.length < 2) return 0;
    return Math.max(0, (new Date(trail[trail.length - 1].fixTime).getTime() - new Date(trail[0].fixTime).getTime()) / 1000);
  }, [trail]);
  const actualDurS = totalSec;

  // Are origin and destination the same place (a typical out-and-back
  // delivery route)? If so, render only ONE house marker labelled "Casa"
  // so the operator doesn't see two icons stacked at the same coords.
  const sameOriginDest = useMemo(() => {
    if (!origin || !destination) return false;
    return haversineM(origin, destination) < 30;
  }, [origin, destination]);

  // As the animated marker moves, mark each OT as "visited" the first
  // time the vehicle gets within ~120m of its delivery point. Once
  // visited, the OT card slides into the left rail and stays there.
  // We use a 120m threshold because urban GPS fixes drift and we'd
  // rather mark a delivery a couple of fixes early than miss it.
  useEffect(() => {
    if (!animPos || !orders.length) return;
    setVisitedOrderIds(prev => {
      const set = new Set(prev);
      let changed = false;
      for (const o of orders) {
        if (set.has(o.id)) continue;
        if (haversineM(animPos, { lat: o.lat, lng: o.lng }) < 120) {
          set.add(o.id);
          changed = true;
        }
      }
      if (!changed) return prev;
      // Preserve order of first visit.
      const visited = [...prev];
      for (const o of orders) if (set.has(o.id) && !visited.includes(o.id)) visited.push(o.id);
      return visited;
    });
  }, [animPos, orders]);

  // When scrubbing backwards, drop OTs whose delivery point is past
  // the current animation position.
  useEffect(() => {
    if (!animPos || !orders.length || !visitedOrderIds.length) return;
    // Determine which visited OTs are still "behind" the marker by
    // computing where each one falls along the trail. Cheaper proxy:
    // an OT only counts as visited if the marker has actually been
    // at that point — when scrubbing back near the start, clear any
    // OT located further along the route.
    const curIdx = intIdx;
    const nearByPriorIdx = new Set();
    for (const o of orders) {
      // Find first trail index closer than 120m to the OT's delivery.
      const firstNearIdx = playTrail.findIndex(p => haversineM(p, o) < 120);
      if (firstNearIdx >= 0 && firstNearIdx <= curIdx) nearByPriorIdx.add(o.id);
    }
    const filtered = visitedOrderIds.filter(id => nearByPriorIdx.has(id));
    if (filtered.length !== visitedOrderIds.length) setVisitedOrderIds(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intIdx]);

  // Build the title's customRef list.
  const customRefsLabel = useMemo(() => {
    const refs = orders.map(o => o.customRef).filter(Boolean);
    if (!refs.length) return '';
    if (refs.length <= 4) return refs.join(' · ');
    return `${refs.slice(0, 3).join(' · ')} +${refs.length - 3}`;
  }, [orders]);

  const visitedOrders = useMemo(
    () => visitedOrderIds.map(id => orders.find(o => o.id === id)).filter(Boolean),
    [visitedOrderIds, orders]
  );

  // Helper that fits the map to the entire route — GPS trail, planned
  // geometry, casa markers and waypoints. Used both for the initial
  // load, when playback starts (slightly tighter padding so the route
  // fills more of the viewport), and as a manual "recentrar" button.
  const fitFullRoute = useCallback((tight = false) => {
    if (!mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    [...trail, ...planned].forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (origin) bounds.extend(origin);
    if (destination) bounds.extend(destination);
    stops.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
    if (bounds.isEmpty()) return;
    const pad = tight
      ? { top: 24, bottom: 56, left: 24, right: 24 }
      : { top: 60, bottom: 80, left: 60, right: 60 };
    mapRef.current.fitBounds(bounds, pad);
  }, [trail, planned, origin, destination, stops]);

  // On play start, fit the full route so the operator sees the whole
  // trajectory before the follow-camera kicks in.
  useEffect(() => {
    if (!playing || !mapRef.current) return;
    fitFullRoute(true);
  }, [playing, fitFullRoute]);

  // Follow-camera: pan the map to the moving marker while playing.
  // Throttled with rAF so it stays smooth at 60fps without thrashing.
  // Detects manual user gestures (drag/zoom) and turns follow OFF —
  // operator can re-engage by clicking the "Seguir veículo" button.
  const lastPanRef = useRef(0);
  useEffect(() => {
    if (!playing || !followCamera || !mapRef.current || !animPos) return;
    const now = performance.now();
    // Throttle to ~5 pans/second — Google Maps does its own interpolation
    // between panTo calls so we don't need 60Hz updates.
    if (now - lastPanRef.current < 200) return;
    lastPanRef.current = now;
    try { mapRef.current.panTo(animPos); } catch { /* map not ready */ }
  }, [playing, followCamera, animPos]);

  // Detect user-initiated map gestures so we don't fight them. Google
  // fires `dragstart` and `zoom_changed` on both programmatic and user
  // moves — to tell them apart we listen for `dragstart` (only user)
  // and a wheel/touch event on the map container.
  useEffect(() => {
    if (!mapRef.current || !open) return;
    const map = mapRef.current;
    const onDragStart = () => setFollowCamera(false);
    const listener = window.google.maps.event.addListener(map, 'dragstart', onDragStart);
    return () => { try { window.google.maps.event.removeListener(listener); } catch {} };
  }, [open, isLoaded]);

  // Manual "recentrar percurso" CTA — same logic, default padding.
  const recenterBounds = useCallback(() => fitFullRoute(false), [fitFullRoute]);

  const handlePlayPause = useCallback(() => {
    // Recomeça no início da janela quando está no fim (ou fora dela).
    if (idx >= sel.b || idx < sel.a) setIdx(sel.a);
    setPlaying(p => !p);
  }, [idx, sel.a, sel.b]);
  const handleReplay = useCallback(() => {
    setIdx(sel.a);
    setPlaying(true);
  }, [sel.a]);
  const handleSeek = useCallback((v) => {
    setPlaying(false);
    setIdx(Math.max(0, Math.min(v, playTrail.length - 1)));
  }, [playTrail.length]);
  const handleSelChange = useCallback((a, b) => {
    const last = Math.max(0, playTrail.length - 1);
    setSel({ a: Math.max(0, Math.min(a, last)), b: Math.max(0, Math.min(b, last)) });
  }, [playTrail.length]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: classes.paper }}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <PlayArrowIcon style={{ fontSize: 22 }} />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography className={classes.title} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Replay — {routeName || `Rota #${routeId}`}
            {customRefsLabel && (
              <span style={{ color: '#4338ca', fontWeight: 700, marginLeft: 8 }}>
                · {customRefsLabel}
              </span>
            )}
          </Typography>
          {/* Badges em vez do subtítulo monolítico — uma "pill" por dado
              estruturado: matrícula (escura, tipográfica de monoespaçada),
              motorista, estado da rota, e data de execução. Cabeçalho
              passa a ler como uma ficha — o operador identifica o veículo
              à primeira sem precisar de ler o subtítulo todo. */}
          <Box className={classes.headerBadges}>
            {vehiclePlate && (
              <Box className={`${classes.headerBadge} ${classes.headerBadgePlate}`} title="Matrícula do veículo">
                🚛 {vehiclePlate}
              </Box>
            )}
            {driverName && (
              <Box className={classes.headerBadge} title="Motorista">
                👤 {driverName}
              </Box>
            )}
            {routeStatus && (() => {
              const map = {
                completed: { cls: classes.headerBadgeStatusDone, label: 'CONCLUÍDA', dot: '#16a34a' },
                in_progress: { cls: classes.headerBadgeStatusLive, label: 'EM CURSO', dot: '#f59e0b' },
              };
              const cfg = map[routeStatus] || { cls: classes.headerBadgeStatusOther, label: routeStatus.toUpperCase(), dot: '#94a3b8' };
              return (
                <Box className={`${classes.headerBadge} ${cfg.cls}`}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                  {cfg.label}
                </Box>
              );
            })()}
            {(actualStartedAt || actualCompletedAt) && (
              <Box className={classes.headerBadge} title="Data de execução">
                📅 {new Date(actualCompletedAt || actualStartedAt).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
              </Box>
            )}
            {trail.length > 0 && (
              <Box className={classes.headerStats} title="Estatísticas do percurso real">
                {trail.length} fixes · {fmtClock(totalSec)}
                {orders.length > 0 && ` · ${orders.length} ${orders.length === 1 ? 'OT' : 'OTs'}`}
              </Box>
            )}
            {trail.length === 0 && !loading && !error && (
              <Typography className={classes.subtitle}>
                A obter percurso real do EcooGPS…
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        {loading && (
          <Box className={classes.loading}>
            <CircularProgress size={24} />
            <Typography style={{ fontSize: '0.78rem', color: '#475569' }}>A obter percurso real do EcooGPS…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Box className={classes.empty}>
            <Box className={classes.emptyCard}>
              <Box className={classes.emptyIcon} style={{ background: 'linear-gradient(135deg, #fee2e2, #fecaca)', borderColor: '#fca5a5' }}>
                ⚠
              </Box>
              <Typography className={classes.emptyTitle} style={{ color: '#b91c1c' }}>
                Não foi possível carregar o percurso
              </Typography>
              <Typography className={classes.emptyBody}>{error}</Typography>
              <Box className={classes.emptyHint}>
                💡 A rota tem de estar em curso ou concluída, e o veículo associado tem de ter reportado GPS via EcooGPS.
              </Box>
            </Box>
          </Box>
        )}
        {!loading && !error && trail.length < 2 && (() => {
          // Diagnóstico do trail vazio. Com o report fallback do EcooGPS
          // (commit f1af7f5) o backend já tenta tanto /api/positions
          // (24h Traccar) como /report/report_routechartdata.php
          // (histórico completo). Se mesmo assim vier vazio, as causas
          // prováveis são:
          //   • Rota nunca executada → não há nada para registar.
          //   • Veículo desligado / sem GPS durante a execução.
          //   • Veículo Traccar errado associado à rota.
          const everExecuted = !!(actualStartedAt || actualCompletedAt);
          let title, body, hint, icon;
          if (!everExecuted) {
            title = 'Rota ainda não foi executada';
            body = 'Esta rota não tem registo de partida real — para teres um replay, o motorista tem de a iniciar (botão "Iniciar rota" na app) e percorrê-la com o veículo associado.';
            hint = '💡 Volta cá quando a rota estiver "em curso" ou "concluída".';
            icon = '🚦';
          } else {
            title = 'Veículo não reportou GPS durante esta rota';
            body = 'A rota foi executada mas o veículo associado não enviou posições suficientes para construir um percurso (≥ 2 fixes), nem no histórico recente nem nos relatórios completos do EcooGPS.';
            hint = '💡 Verifica se o veículo correcto está atribuído à rota e se o tracker EcooGPS estava ligado durante toda a execução.';
            icon = '📡';
          }
          return (
            <Box className={classes.empty}>
              <Box className={classes.emptyCard}>
                <Box className={classes.emptyIcon}>{icon}</Box>
                <Typography className={classes.emptyTitle}>{title}</Typography>
                <Typography className={classes.emptyBody}>{body}</Typography>
                <Box className={classes.emptyHint}>{hint}</Box>
              </Box>
            </Box>
          );
        })()}

        {/* HUD overlay with current state */}
        {!loading && !error && cur && (
          <Box className={classes.hud}>
            <Box className={classes.hudRow}>
              <span className={classes.hudLabel}>Hora</span>
              <span>{fmtTime(cur.fixTime)}</span>
            </Box>
            <Box className={classes.hudRow}>
              <span className={classes.hudLabel}>Velocidade</span>
              <span>{Math.round(cur.speed || 0)} km/h</span>
            </Box>
            <Box className={classes.hudRow}>
              <span className={classes.hudLabel}>Decorrido</span>
              <span>{fmtClock(elapsedSec)} / {fmtClock(totalSec)}</span>
            </Box>
            {cur.address && (
              <Box className={classes.hudRow} style={{ maxWidth: 280 }}>
                <span className={classes.hudLabel}>Local</span>
                <span style={{ fontSize: '0.66rem', fontWeight: 500, opacity: 0.95 }}>
                  {String(cur.address).split(',')[0]}
                </span>
              </Box>
            )}
          </Box>
        )}

        {/* OT cards — slide in as the vehicle passes by each delivery */}
        {!loading && !error && visitedOrders.length > 0 && (
          <Box className={classes.otStack}>
            {visitedOrders.map((o) => {
              const stColor = o.status === 'delivered' ? '#16a34a'
                : o.status === 'in_transit' ? '#3b82f6'
                : o.status === 'cancelled' ? '#dc2626'
                : '#64748b';
              const stBg = o.status === 'delivered' ? '#dcfce7'
                : o.status === 'in_transit' ? '#dbeafe'
                : o.status === 'cancelled' ? '#fef2f2'
                : '#f1f5f9';
              const sIcon = o.serviceType === 'assistance' ? '🔧'
                : o.serviceType === 'fuel' ? '⛽' : '📦';
              return (
                <Box key={o.id} className={classes.otCard}
                  style={{ borderLeftColor: stColor }}>
                  <Box className={classes.otCardHeader}>
                    <span className={classes.otSeq} style={{ background: stColor }}>{o.seq}</span>
                    <span className={classes.otRef}>{sIcon} {o.customRef}</span>
                    <span className={classes.otStatus} style={{ background: stBg, color: stColor }}>
                      {o.status}
                    </span>
                  </Box>
                  <Typography className={classes.otName}>{o.deliveryName}</Typography>
                  {o.deliveryAddress && o.deliveryAddress !== o.deliveryName && (
                    <Typography className={classes.otAddr}>{o.deliveryAddress}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Comparative metrics panel — top-right. Shows planned vs actual
            distance/duration and the peak deviation. Stays visible the
            whole replay so the client can grasp the comparison at a
            glance. Hidden until we have enough data to compute it. */}
        {!loading && !error && trail.length >= 2 && (plannedDistM > 0 || actualDistM > 0) && (
          <Box className={classes.metrics}>
            <Typography className={classes.metricsTitle}>Planeado vs Real</Typography>
            <Box className={classes.metricsRow}>
              <span className={classes.metricsLabel}>Distância</span>
              <span className={classes.metricsPlan}>{plannedDistM > 0 ? `${(plannedDistM / 1000).toFixed(1)} km` : '—'}</span>
              <span className={classes.metricsReal}>{actualDistM > 0 ? `${(actualDistM / 1000).toFixed(1)} km` : '—'}</span>
            </Box>
            <Box className={classes.metricsRow}>
              <span className={classes.metricsLabel}>Duração</span>
              <span className={classes.metricsPlan}>{plannedDurS > 0 ? fmtClock(plannedDurS) : '—'}</span>
              <span className={classes.metricsReal}>{actualDurS > 0 ? fmtClock(actualDurS) : '—'}</span>
            </Box>
            {plannedDistM > 0 && actualDistM > 0 && (
              <Box className={classes.metricsRow}>
                <span className={classes.metricsLabel}>Δ km</span>
                <span style={{ gridColumn: 'span 2', textAlign: 'right', fontSize: '0.66rem', fontWeight: 700, color: actualDistM > plannedDistM ? '#fca5a5' : '#86efac' }}>
                  {actualDistM > plannedDistM ? '+' : '−'}{Math.abs((actualDistM - plannedDistM) / 1000).toFixed(1)} km
                </span>
              </Box>
            )}
            {deviationMax && (
              <Box className={classes.metricsBig}>
                <span className={classes.metricsBigLabel}>Desvio máx.</span>
                <span className={classes.metricsBigValue}>{Math.round(deviationMax.dist)} m</span>
              </Box>
            )}
          </Box>
        )}

        {/* Follow-vehicle toggle — discrete pill bottom-right. Lights up
            when active. Operator can drag the map manually to break out
            of follow mode; this button is how they get back in. */}
        {!loading && !error && trail.length >= 2 && (
          <Button
            className={`${classes.followToggle} ${followCamera ? classes.followToggleActive : ''}`}
            size="small"
            onClick={() => setFollowCamera(f => !f)}
          >
            {followCamera ? '🎯 A seguir veículo' : '○ Seguir veículo'}
          </Button>
        )}

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            options={mapOptions}
            onLoad={(m) => { mapRef.current = m; }}
            zoom={12}
            center={origin || { lat: 38.7, lng: -9.1 }}
          >
            {/* No TrafficLayer — operator asked to keep the map clean
                for client-facing replays. */}
            {/* Rota planeada — linha azul de referência POR BAIXO. Fica
                tapada pelos runs verdes (onde o motorista cumpriu) e
                visível ao lado dos runs vermelhos (onde houve desvio,
                porque o vermelho está noutra rua). */}
            {planned.length > 1 && (
              <Polyline path={planned} options={{
                strokeColor: '#2563eb', strokeOpacity: 0.85, strokeWeight: 5, clickable: false, zIndex: 1,
              }} />
            )}
            {/* Preview translúcido — todos os runs do trail real (futuro
                + passado), verde=cumpriu, vermelho=desvio. Opacity baixa
                porque é só guia visual; os runs sólidos (a seguir) por
                cima é que mostram o progresso da animação. */}
            {allRealRuns.map((r, i) => (
              <Polyline key={`pv-${i}`} path={r.path} options={{
                strokeColor: r.matched ? '#16a34a' : '#dc2626',
                strokeOpacity: 0.30, strokeWeight: 4, clickable: false, zIndex: 2,
              }} />
            ))}
            {/* Sólidos por cima — apenas até ao ponto actual da
                animação. O verde tapa a linha azul nas zonas em que
                o motorista cumpriu; o vermelho fica destacado paralelo
                à azul nas zonas de desvio. */}
            {passedRealRuns.map((r, i) => (
              <Polyline key={`pa-${i}`} path={r.path} options={{
                strokeColor: r.matched ? '#16a34a' : '#dc2626',
                strokeOpacity: 0.98, strokeWeight: 5, clickable: false, zIndex: 3,
              }} />
            ))}
            {/* Synthetic "return home" leg — dashed grey, drawn only when
                the GPS history ended short of the destination. */}
            {passedSyntheticTrail.length > 1 && (
              <Polyline path={passedSyntheticTrail} options={{
                strokeColor: '#64748b', strokeOpacity: 0.6, strokeWeight: 3, clickable: false, zIndex: 3,
                icons: [{
                  icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                  offset: '0', repeat: '14px',
                }],
                geodesic: false,
              }} />
            )}
            {/* Deviation peak marker — a single discrete pin at the
                point where the actual trail strayed furthest from the
                planned polyline. Subtle but conveys a metric clients
                love to see. */}
            {deviationMax && deviationMax.point && (
              <Marker position={deviationMax.point}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: '#f59e0b', fillOpacity: 1,
                  strokeColor: '#fff', strokeWeight: 2.5,
                  scale: 7,
                }}
                title={`Desvio máximo do trajecto planeado: ${Math.round(deviationMax.dist)} m`}
                zIndex={45}
              />
            )}
            {/* Pins de partida/chegada — quando coincidem (out-and-back)
                renderiza só uma casa índigo com label "Partida/Chegada";
                quando são pontos distintos, casa verde na partida e
                casa vermelha na chegada para o operador identificar
                imediatamente onde a rota arranca e termina. */}
            {sameOriginDest && origin && (
              <Marker position={origin}
                icon={houseMarker('#6366f1')}
                label={{ text: 'Partida/Chegada', color: '#fff', fontSize: '10px', fontWeight: '700', className: 'house-label' }}
                title={origin.name || 'Partida e Chegada'} />
            )}
            {!sameOriginDest && origin && (
              <Marker position={origin}
                icon={houseMarker('#16a34a')}
                label={{ text: 'Partida', color: '#fff', fontSize: '10px', fontWeight: '700', className: 'house-label' }}
                title={origin.name || 'Partida'} />
            )}
            {!sameOriginDest && destination && (
              <Marker position={destination}
                icon={houseMarker('#dc2626')}
                label={{ text: 'Chegada', color: '#fff', fontSize: '10px', fontWeight: '700', className: 'house-label' }}
                title={destination.name || 'Chegada'} />
            )}
            {/* Pins dos locais de serviço (OTs) — numerados pela ordem
                em que aparecem na rota. Cor depende do estado da OT
                (verde=entregue, azul=em trânsito, vermelho=cancelada,
                cinza=pendente). Tooltip mostra o customRef e a morada. */}
            {orders.map((o) => {
              const color = o.status === 'delivered' ? '#16a34a'
                : o.status === 'in_transit' ? '#3b82f6'
                : o.status === 'cancelled' ? '#dc2626'
                : '#64748b';
              return (
                <Marker
                  key={`ot-${o.id}`}
                  position={{ lat: o.lat, lng: o.lng }}
                  icon={otStopMarker(o.seq, color)}
                  title={`${o.customRef} · ${o.deliveryName}${o.deliveryAddress && o.deliveryAddress !== o.deliveryName ? ` — ${o.deliveryAddress}` : ''}`}
                  zIndex={40}
                />
              );
            })}
            {/* Waypoints adicionais (não-OT) — apenas se a rota tiver
                paragens explícitas que não venham das OTs. Em rotas
                novas isto está tipicamente vazio. */}
            {stops.map((s) => (
              <Marker key={s.idx} position={{ lat: s.lat, lng: s.lng }}
                label={{ text: String(s.idx), color: '#fff', fontSize: '10px', fontWeight: '700' }}
                icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: '#f59e0b', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 9 }}
                title={s.name} />
            ))}
            {/* Pulse halo — a Circle that grows and fades around the
                vehicle. Keeps the eye locked onto the moving marker
                without distracting from the trail underneath. */}
            {animPos && pulseRadius > 0 && (
              <Circle
                center={animPos}
                radius={pulseRadius}
                options={{
                  fillColor: '#dc2626',
                  fillOpacity: pulseOpacity * 0.55,
                  strokeColor: '#dc2626',
                  strokeOpacity: pulseOpacity,
                  strokeWeight: 1.5,
                  clickable: false,
                  zIndex: 49,
                }}
              />
            )}
            {/* Animated vehicle — bubble with plate + driver name, truck
                rotated by the smoothed heading. Same visual style as the
                live map (see [MapViewGoogle.js] buildVehicleIcon) so the
                client recognises it instantly. */}
            {animPos && (
              <Marker
                position={animPos}
                icon={buildVehicleIcon({
                  plate: vehiclePlate,
                  driverName: driverName,
                  heading: smoothedHeading,
                  state: playing ? 'moving' : 'playback',
                  type: vehicleType,
                })}
                zIndex={50}
              />
            )}
          </GoogleMap>
        )}
      </Box>

      {/* Playback controls */}
      <Box className={classes.controls}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title={playing ? 'Pausar' : (idx >= playTrail.length - 1 ? 'Recomeçar' : 'Reproduzir')}>
            <IconButton size="small" className={classes.playBtn}
              onClick={handlePlayPause} disabled={playTrail.length < 2}>
              {playing ? <PauseIcon /> : (idx >= playTrail.length - 1 ? <ReplayIcon /> : <PlayArrowIcon />)}
            </IconButton>
          </Tooltip>
          <Tooltip title="Recomeçar">
            <IconButton size="small" onClick={handleReplay} disabled={playTrail.length < 2} style={{ color: '#64748b' }}>
              <ReplayIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recentrar percurso completo">
            <IconButton size="small" onClick={recenterBounds} disabled={playTrail.length < 2} style={{ color: '#64748b' }}>
              <CenterFocusStrongIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, paddingRight: 8, flex: 1, minWidth: 0 }}>
          <span className={classes.scrubMark} style={{ minWidth: 40, textAlign: 'right' }}>{fmtClock(elapsedSec)}</span>
          <Box style={{ flex: 1, minWidth: 120 }}>
            <SpeedScrubChart
              trail={playTrail}
              idx={idx}
              onSeek={handleSeek}
              sel={sel}
              onSelChange={handleSelChange}
              disabled={playTrail.length < 2}
            />
          </Box>
          <span className={classes.scrubMark} style={{ minWidth: 40 }}>{fmtClock(totalSec)}</span>
        </Box>
        <Box className={classes.speedRow}>
          <SpeedIcon style={{ fontSize: 16, color: '#64748b' }} />
          {SPEEDS.map(s => (
            <Button key={s} size="small"
              className={`${classes.speedBtn} ${speed === s ? classes.speedBtnActive : ''}`}
              onClick={() => setSpeed(s)}>
              {s}×
            </Button>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
}
